import { createUseStyles } from "react-jss";
import { ChoiceListDisplayType } from "../../models/enums/ChoiceListDisplayType";
import { resolveCarrouselCornerBorder } from "../../theme/carrouselThemeTokens";
import * as ChoiceTokens from "../../theme/choiceThemeTokens";
import { shouldUseModernTheme } from "../../theme/navigationButtonThemeColors";
import type { Theme } from "../../types/Theme";

const DESKTOP_DIMENSIONS = 376;
const MOBILE_VIEW_GAP = 16;
const DESKTOP_VIEW_GAP = 64;

const MODERN_STATEMENT_BOX_SHADOW =
    "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)";
const MODERN_STATEMENT_BOX_SHADOW_INACTIVE =
    "0 10px 15px -3px rgb(0 0 0 / 0), 0 4px 6px -4px rgb(0 0 0 / 0)";
const LEGACY_STATEMENT_BOX_SHADOW = "0px 0px 6px 1px rgba(0, 0, 0, 0.20)";
const LEGACY_STATEMENT_BOX_SHADOW_INACTIVE = "0px 0px 6px 1px rgba(0, 0, 0, 0)";

/** Interpolatable box-shadow values for smooth active/inactive transitions. */
export function resolveStatementBoxShadow(theme: Theme, isActiveItem: boolean): string {
    if (shouldUseModernTheme(theme)) {
        return isActiveItem ? MODERN_STATEMENT_BOX_SHADOW : MODERN_STATEMENT_BOX_SHADOW_INACTIVE;
    }
    return isActiveItem ? LEGACY_STATEMENT_BOX_SHADOW : LEGACY_STATEMENT_BOX_SHADOW_INACTIVE;
}

export default createUseStyles({
    srOnly: {
        position: "absolute",
        width: "1px",
        height: "1px",
        padding: 0,
        margin: "-1px",
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        borderWidth: 0,
    },
    statementSlideWrapper: {
        transition: "transform 0.8s ease-in-out",
        "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
        },
    },
    bulletIndicatorItem: {
        transition: "transform 0.8s ease-in-out",
        "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
        },
    },
    container: {
        position: "relative",
        width: ({ isMobileView }) => (isMobileView ? "100%" : "auto"),
        display: "flex",
        flexDirection: "column",
        gap:({isMobileView, isAboveChoiceNavigation})=> (isMobileView || !isAboveChoiceNavigation) ? "8px" : "16px",
    },
    statementsContainer: {
        width: "100%",
        display: "flex",
        overflowX: "hidden",
        scrollBehavior: "smooth",
        gap: ({ isMobileView }) =>
            isMobileView ? `${MOBILE_VIEW_GAP}px` : `${DESKTOP_VIEW_GAP}px`,
        padding: ({ theme }: { theme: Theme }) =>
            shouldUseModernTheme(theme) ? "24px 0px" : "8px 0px",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        "& > div .lottie-gesture": {
            width: "61px",
            height: "30px",
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)"
        },
    },
    statementShadow: {
        transition: "box-shadow 0.3s ease-in-out, border-color 0.3s ease-in-out",
        "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
        },
    },
    statement: {
        width: ({ statementWidth }) => `${statementWidth}px`,
        color: "#555555",
        textAlign: "center",
        minWidth: ({ isMobileView }) => (isMobileView ? "100%" : ""),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflow: "hidden",
        minHeight:({ theme }: { theme: Theme }) => shouldUseModernTheme(theme) ? "112px" : "",
        justifyContent: ({ justifyContent }) => justifyContent,
        border: ({ theme, isActiveItem }: { theme: Theme; isActiveItem?: boolean }) =>
            resolveCarrouselCornerBorder(theme, {
                inactive: shouldUseModernTheme(theme) && isActiveItem === false,
            }),
        borderRadius: ({ theme, statementWidth }) => {
            let calDimension = statementWidth;
            return `${
                theme.ShowStatementBackgroundSettings
                    ? Math.min(
                          theme.CustomBorderValue
                              ? theme.StatementBorderRadius
                              : theme.ChoiceListBackgroundBorderRadius,
                          calDimension / 2,
                      )
                    : ""
            }px`;
        },
        "& .statementTextContent": {
            padding: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: "1.2",
            opacity: ({ isActiveItem, isShowLottieGesture, imageUrl }) => {
                if (isShowLottieGesture && !imageUrl) {
                    return "0.2";
                } else {
                    return !isActiveItem ? "0" : "1";
                }
            },
            transition: "opacity 0.3s ease-in",
            "@media (prefers-reduced-motion: reduce)": {
                transition: "none",
            },
            width: "100%",
            boxSizing: "border-box",
        },

        "& .statementTextContent:empty": {
            display: "none",
        },
        "& .image-wrapper": {
            display: "flex",
            justifyContent: "center",
            backgroundColor: ({ isActiveItem }) => (!isActiveItem ? "#F2F2F2" : ""),
            width: ({ isMobileView }) => (isMobileView ? "100%" : `${DESKTOP_DIMENSIONS}px`),
            flex: 1,
            "& .image-element": {
                opacity: ({ isActiveItem, isShowLottieGesture }) => {
                    if (isShowLottieGesture) {
                        return "0.2";
                    } else {
                        return !isActiveItem ? "0" : "1";
                    }
                },
                transition: "opacity 0.3s ease-in",
                "@media (prefers-reduced-motion: reduce)": {
                    transition: "none",
                },
                borderRadius: ({ theme }) => {
                    if (!theme.ShowStatementBackgroundSettings || !theme.CustomBorderValue) {
                        if (theme.ChoiceListBackgroundBorderRadius === 100) return "20px";
                        if (theme.ChoiceListBackgroundBorderRadius === 15) return "8px";
                        // "Soft" is 15 on classic themes but 8 on modern ones (theme editor
                        // ChoicesBackgroundCorners) — both map to the same image radius.
                        if (
                            shouldUseModernTheme(theme) &&
                            theme.ChoiceListBackgroundBorderRadius === 8
                        ) {
                            return "8px";
                        }
                    }

                    if (
                        theme.ShowStatementBackgroundSettings &&
                        theme.StatementBackgroundAlpha === 0
                    ) {
                        return `${theme.StatementBorderRadius}px`;
                    }
                    return "";
                },
            },
        },
    },
    choiceAndNavigationContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: ({
            choiceListDisplayType,
        }: {
            choiceListDisplayType: ChoiceListDisplayType;
        }) => (choiceListDisplayType === ChoiceListDisplayType.Horizontal ? "center" : ""),
        gap:({ isMobileView })=> isMobileView ? "16px" : "24px",
    },
    navigationButtonsPanel: {
        textAlign: "center",
        display: "flex",
        justifyContent: "center",
        margin: "0 auto",
        alignItems: "center",
        minWidth: "192px",
        gap: "16px",
        "& .fixedWidth": {
            flex: "0 0 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "16px",
            height: "16px",
            padding: "8px 0px",
            color: ({ theme }: { theme: Theme }) =>
                shouldUseModernTheme(theme)
                    ? ChoiceTokens.resolveModernChoiceLabelColor(theme)
                    : undefined,
            transition: ({ theme }: { theme: Theme }) =>
                shouldUseModernTheme(theme) ? "background 200ms, color 200ms" : undefined,
            "&:not([aria-disabled='true']):not([aria-hidden='true']):hover": {
                background: ({ theme }: { theme: Theme }) =>
                    shouldUseModernTheme(theme)
                        ? ChoiceTokens.resolveChoiceBackgroundHover(theme)
                        : undefined,
            },
            "& svg": {
                width: "16px",
                color: ({ theme }: { theme: Theme }) =>
                    shouldUseModernTheme(theme) ? "currentColor" : undefined,
            },
        },
        "& .counter-navigation": {
        minWidth : "110px",
        },
    },
    navigationButton: {
        height: "100%",
        cursor: "pointer",
        color: ({ theme }: { theme: Theme }) =>
            shouldUseModernTheme(theme) ? "currentColor" : "#667085",
        // chevron-left/chevron-right are mirror images of each other, so flipping both
        // horizontally swaps their visual meaning for RTL without needing separate icons.
        // Must return a concrete value (not undefined) in both branches — JSS treats an
        // undefined dynamic value as "leave the previous value alone", not "clear it", so
        // switching back to LTR would otherwise leave the RTL flip stuck.
        transform: ({ isRtl }: { isRtl?: boolean }) => (isRtl ? "scaleX(-1)" : "none"),
    },
    bulletNavigationPanel: {
        display: "flex",
        gap: "16px",
        alignItems: "center",
        justifyContent: "center",
        "& .ellipse-item": {
            width: "16px",   
            height: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& div": {
                width: "6px",   
                height: "6px",
                borderRadius: "50%",
                backgroundColor: "#667085",
            }
        },
        "& .bullets-container":{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            maxWidth: "136px",
            overflow: "hidden",
            padding: "3px",
            "& > div":{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& svg": {
                    width: "16px",
                    height: "16px",
                }
            },
            "& .active-answered-item": {
                outline: "2px solid #316AEE",
                display: "flex",
                alignItems: "center",
                borderRadius: "50%",
                "& svg": {
                    width: "16px",
                    height: "16px",
                }
            },
            "& .active-not-answered-item": {
                outline: "3px solid #D0D3DD",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                "& svg": {
                    width: "16px",
                    height: "16px",
                }
            },
            "& .bullet": {
                width: "16px",
                height: "16px",
                backgroundColor: "#D0D3DD",
                borderRadius: "50%",
            },
        },
       
    }
});
